/**
 * Engine service — business-logic boundary for WorkflowRule-centric operations.
 *
 * UI mapping:
 *   Evaluator  ← EvaluationRule (backend /engine/evaluation/rules)
 *   Automation ← AutomationRule (backend /engine/automation/rules)
 */
import { apiv1 as api } from '@/adapters';
import type {
  AutomationAction,
  AutomationRule as AutomationRuleModel,
  Condition,
  EvaluationRule as EvaluationRuleModel,
  EvaluationRuleUpdateInput,
  EvaluatorTrigger as EvaluatorTriggerModel,
  EventOutput,
  evaluationRuleCreateOpenAPIDoc,
  Filter,
} from '@/adapters/apiv1.g';
import { unwrapData } from '@/adapters/shared';
import type {
  Automation,
  Evaluator,
  CreateEvaluatorPayload,
  EvaluatorTrigger,
  RuleAction,
  RuleCondition,
  RuleFilter,
  RuleOutput,
  RuleTrigger,
  WorkflowRule,
} from '@/types/core/workflow-rule';
import type { Page, PageRequest } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TEMPLATE_PAYLOAD_KEY = 'template';

type VersionedModel = {
  _rev?: number;
  version?: number;
};

function toPage<T extends { name?: string; description?: string }>(
  items: T[],
  query?: PageRequest
): Page<T> {
  const filtered = applyLocalFilter(items, query?.filter);
  const limit = query?.limit ?? filtered.length;
  return {
    items: filtered.slice(0, limit),
    next: null,
    prev: null,
    limit,
    total: filtered.length,
  };
}

function applyLocalFilter<T extends { name?: string; description?: string }>(
  items: T[],
  filter?: PageRequest['filter']
): T[] {
  if (typeof filter !== 'string' || filter.trim() === '') {
    return items;
  }

  const needle = filter.trim().toLowerCase();
  return items.filter((item) =>
    `${item.name ?? ''} ${item.description ?? ''}`
      .toLowerCase()
      .includes(needle)
  );
}

function extractVersion(model?: VersionedModel): number | undefined {
  return model?._rev ?? model?.version;
}

function extractTrigger(trigger?: {
  event_type?: string;
  event_space?: string;
}): RuleTrigger | undefined {
  if (!trigger) return undefined;
  return {
    eventType: trigger.event_type ?? '',
    eventSpace: trigger.event_space ?? '',
  };
}

function serializeTrigger(
  trigger?: RuleTrigger
): { event_type?: string; event_space?: string } | undefined {
  if (!trigger) return undefined;
  return {
    event_type: trigger.eventType,
    event_space: trigger.eventSpace,
  };
}

function extractFilters(filters?: Filter[]): RuleFilter[] | undefined {
  return filters?.map((filter) => ({
    field: filter.field ?? '',
    operator: (filter.operator ?? 'equals') as RuleFilter['operator'],
    value: filter.value,
  }));
}

function serializeFilters(filters?: RuleFilter[]): Filter[] | undefined {
  return filters?.map((filter) => ({
    field: filter.field,
    operator: filter.operator,
    value: filter.value as string,
  }));
}

function extractEvaluatorTriggers(
  triggers?: EvaluatorTriggerModel[]
): EvaluatorTrigger[] {
  if (!triggers) return [];
  return triggers.map((t) => ({
    eventType: t.event_type ?? '',
    eventSpace: t.event_space ?? '',
    filters: extractFilters(t.filters) ?? [],
    priority: t.priority ?? 0,
    window: t.window
      ? {
          threshold: t.window.threshold ?? 0,
          durationSeconds: t.window.duration_seconds ?? 0,
        }
      : undefined,
  }));
}

function serializeEvaluatorTriggers(
  triggers: EvaluatorTrigger[]
): EvaluatorTriggerModel[] {
  return triggers.map((t) => ({
    event_type: t.eventType,
    event_space: t.eventSpace,
    filters: serializeFilters(t.filters),
    priority: t.priority,
    window: t.window
      ? {
          threshold: t.window.threshold,
          duration_seconds: t.window.durationSeconds,
        }
      : undefined,
  }));
}

function extractCondition(condition?: Condition): RuleCondition | undefined {
  if (!condition) return undefined;
  return {
    type: (condition.type ?? '') as RuleCondition['type'],
    parameters: (condition.parameters ?? {}) as Record<string, unknown>,
  };
}

function serializeCondition(condition?: RuleCondition): Condition | undefined {
  if (!condition) return undefined;
  return {
    type: condition.type,
    parameters: Object.fromEntries(
      Object.entries(condition.parameters ?? {}).map(([key, value]) => [
        key,
        String(value),
      ])
    ),
  };
}

function extractEventOutput(
  output?: EventOutput
): Extract<RuleOutput, { type: 'event' }> {
  const payloadTemplate = (output?.payload_template ?? {}) as Record<
    string,
    unknown
  >;
  const context: Record<string, unknown> = { ...payloadTemplate };
  const templateValue = context[TEMPLATE_PAYLOAD_KEY];
  const template =
    typeof templateValue === 'string'
      ? templateValue
      : String(templateValue ?? '');
  delete context[TEMPLATE_PAYLOAD_KEY];

  return {
    type: 'event',
    eventOutput: {
      eventType: output?.event_type ?? '',
      template,
      context,
    },
  };
}

function serializeEventOutput(
  output?: Extract<RuleOutput, { type: 'event' }>
): EventOutput[] | undefined {
  if (!output) return undefined;

  const payloadTemplate: Record<string, string> = Object.fromEntries(
    Object.entries(output.eventOutput.context ?? {}).map(([key, value]) => [
      key,
      String(value),
    ])
  );
  if (output.eventOutput.template.trim() !== '') {
    payloadTemplate[TEMPLATE_PAYLOAD_KEY] = output.eventOutput.template;
  }

  return [
    {
      event_type: output.eventOutput.eventType,
      payload_template:
        Object.keys(payloadTemplate).length > 0 ? payloadTemplate : undefined,
    },
  ];
}

function extractEvaluator(model: EvaluationRuleModel): Evaluator | null {
  if (model.type !== 'derivation') {
    return null;
  }

  return {
    _rev: extractVersion(model as VersionedModel),
    ruleId: model.rule_id as UUID | undefined,
    tenantId: model.tenant_id as UUID | undefined,
    name: model.name,
    description: model.description,
    type: 'derivation',
    enabled: model.enabled,
    status: model.status,
    version: model.version,
    publishedVersion: model.published_version,
    triggers: extractEvaluatorTriggers(model.triggers),
    condition: extractCondition(model.condition),
    output: extractEventOutput(model.outputs?.[0]),
    outputs: model.outputs?.map((o) => ({
      eventType: o.event_type ?? '',
      eventSpace: o.event_space ?? '',
      payloadTemplate: o.payload_template,
      streamSegment: o.stream_segment,
    })),
  };
}

function extractRuleAction(action?: AutomationAction): RuleAction {
  return {
    connectorRef: action?.connector_ref,
    featureRef: action?.feature_ref,
    idempotencyTemplate: action?.idempotency_template,
    payloadTemplate: action?.payload_template,
    inputMapping: action?.input_mapping
      ? {
          fields: action.input_mapping.fields?.map((f) => ({
            from: f.from,
            to: f.to,
          })),
        }
      : undefined,
  };
}

function extractAutomation(model: AutomationRuleModel): Automation {
  const action = model.action as AutomationAction | undefined;
  return {
    _rev: extractVersion(model as VersionedModel),
    ruleId: model.rule_id as UUID | undefined,
    tenantId: model.tenant_id as UUID | undefined,
    name: model.name,
    description: model.description,
    enabled: model.enabled,
    priority: model.priority,
    trigger: extractTrigger(model.trigger),
    filters: extractFilters(model.filters),
    action: extractRuleAction(action),
    output: {
      type: 'action',
      actionOutput: {
        actions: action ? [action] : [],
        continueOnError: false,
      },
    },
  };
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const engineService = {
  // ── Evaluators ──

  listEvaluators: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Evaluator>> => {
    const res = await api.listEvaluationRules(tenantId);
    const models = unwrapData<EvaluationRuleModel[]>(res) ?? [];
    const items = models
      .map(extractEvaluator)
      .filter((rule): rule is Evaluator => rule !== null);
    return toPage(items, query);
  },

  findEvaluator: async (
    tenantId: UUID,
    ruleId: UUID
  ): Promise<Evaluator | null> => {
    const res = await api.findEvaluationRule(tenantId, ruleId);
    const model = unwrapData<EvaluationRuleModel>(res);
    if (!model) return null;
    return extractEvaluator(model);
  },

  createEvaluator: async (
    tenantId: UUID,
    payload: CreateEvaluatorPayload
  ): Promise<UUID> => {
    // OpenAPI wraps embedded EvaluationRule; API accepts flat legacy JSON (see evaluation_rule_create_decode.go).
    const res = await api.createEvaluationRule(tenantId, {
      tenant_id: tenantId,
      name: payload.name,
      description: payload.description,
      enabled: payload.enabled ?? true,
      type: 'derivation',
    } as evaluationRuleCreateOpenAPIDoc);

    const model = unwrapData<EvaluationRuleModel>(res);
    if (!model?.rule_id) {
      throw new Error('Failed to create evaluator');
    }
    return model.rule_id as UUID;
  },

  updateEvaluator: async (
    tenantId: UUID,
    ruleId: UUID,
    updates: Partial<Omit<WorkflowRule, 'ruleId' | 'tenantId' | 'type'>>
  ): Promise<boolean> => {
    const body: EvaluationRuleUpdateInput = {};

    if (updates.name !== undefined) body.name = updates.name;
    if (updates.description !== undefined)
      body.description = updates.description;
    if (updates.enabled !== undefined) body.enabled = updates.enabled;
    if (updates.triggers !== undefined) {
      body.triggers = serializeEvaluatorTriggers(updates.triggers);
    }
    if (updates.condition !== undefined) {
      body.condition = serializeCondition(updates.condition);
    }
    if (updates.output?.type === 'event') {
      body.outputs = serializeEventOutput(updates.output);
    }

    const res = await api.updateEvaluationRule(tenantId, ruleId, body);
    return Boolean(res?.ok);
  },

  deleteEvaluator: async (tenantId: UUID, ruleId: UUID): Promise<boolean> => {
    const res = await api.deleteEvaluationRule(tenantId, ruleId);
    return Boolean(res?.ok);
  },

  changeEvaluatorStatus: async (
    tenantId: UUID,
    ruleId: UUID,
    enabled: boolean
  ): Promise<boolean> => {
    const res = await api.changeEvaluationRuleStatus(tenantId, ruleId, {
      enabled,
    });
    return Boolean(res?.ok);
  },

  publishEvaluator: async (
    tenantId: UUID,
    ruleId: UUID
  ): Promise<Evaluator | null> => {
    const res = await api.publishEvaluationRule(tenantId, ruleId);
    const model = unwrapData<EvaluationRuleModel>(res);
    if (!model) return null;
    return extractEvaluator(model);
  },

  retireEvaluator: async (
    tenantId: UUID,
    ruleId: UUID
  ): Promise<Evaluator | null> => {
    const res = await api.retireEvaluationRule(tenantId, ruleId);
    const model = unwrapData<EvaluationRuleModel>(res);
    if (!model) return null;
    return extractEvaluator(model);
  },

  // ── Automations ──

  listAutomations: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Automation>> => {
    const res = await api.listAutomationRules(tenantId);
    const models = unwrapData<AutomationRuleModel[]>(res) ?? [];
    return toPage(models.map(extractAutomation), query);
  },

  findAutomation: async (
    tenantId: UUID,
    ruleId: UUID
  ): Promise<Automation | null> => {
    const res = await api.findAutomationRule(tenantId, ruleId);
    const model = unwrapData<AutomationRuleModel>(res);
    if (!model) return null;
    return extractAutomation(model);
  },

  createAutomation: async (
    tenantId: UUID,
    payload: Pick<
      AutomationRuleModel,
      'name' | 'description' | 'enabled' | 'priority' | 'trigger' | 'action'
    >
  ): Promise<UUID> => {
    const body: AutomationRuleModel = {
      name: payload.name,
      description: payload.description,
      enabled: payload.enabled ?? true,
      priority: payload.priority ?? 50,
      trigger: payload.trigger,
      action: payload.action,
    };
    const res = await api.createAutomationRule(tenantId, body);
    const created = unwrapData<AutomationRuleModel>(res);
    if (!created?.rule_id) {
      throw new Error('Failed to create automation');
    }
    return created.rule_id as UUID;
  },

  updateAutomation: async (
    tenantId: UUID,
    ruleId: UUID,
    updates: Partial<Omit<WorkflowRule, 'ruleId' | 'tenantId' | 'type'>>
  ): Promise<boolean> => {
    const body: Record<string, unknown> = {};

    if (updates.name !== undefined) body.name = updates.name;
    if (updates.description !== undefined)
      body.description = updates.description;
    if (updates.enabled !== undefined) body.enabled = updates.enabled;
    if (updates.priority !== undefined) body.priority = updates.priority;
    if (updates.trigger !== undefined)
      body.trigger = serializeTrigger(updates.trigger);
    if (updates.filters !== undefined)
      body.filters = serializeFilters(updates.filters);

    const res = await api.updateAutomationRule(tenantId, ruleId, body as never);
    return Boolean(res?.ok);
  },

  deleteAutomation: async (tenantId: UUID, ruleId: UUID): Promise<boolean> => {
    const res = await api.deleteAutomationRule(tenantId, ruleId);
    return Boolean(res?.ok);
  },

  changeAutomationStatus: async (
    tenantId: UUID,
    ruleId: UUID,
    enabled: boolean
  ): Promise<boolean> => {
    const res = await api.changeAutomationRuleStatus(tenantId, ruleId, {
      enabled,
    });
    return Boolean(res?.ok);
  },

  // ── Engine config / runners / events ──

  getEngineConfig: async (tenantId: UUID): Promise<Record<string, unknown>> => {
    const [evaluationRes, automationRes] = await Promise.all([
      api.listEvaluationRules(tenantId),
      api.listAutomationRules(tenantId),
    ]);

    const evaluators = unwrapData<EvaluationRuleModel[]>(evaluationRes) ?? [];
    const automations = unwrapData<AutomationRuleModel[]>(automationRes) ?? [];

    return {
      evaluatorCount: evaluators.length,
      automationCount: automations.length,
    };
  },
};

// ─── Dev mock ─────────────────────────────────────────────────────────────────
// Set VITE_MOCK_ENGINE=true in .env.local to bypass the backend while the
// permission bug is being fixed. All mutations update in-memory state so the
// full create → list → edit → delete flow works without a real backend.

import { newUUID } from '@/utils/uuid';

const _mockStore: Evaluator[] = [
  {
    ruleId: 'aaaaaaaa-0000-0000-0000-000000000001' as UUID,
    tenantId: 'aaaaaaaa-0000-0000-0000-000000000000' as UUID,
    name: 'Account Lockout Evaluator',
    description: 'Fires when an account is locked out after failed logins.',
    type: 'derivation',
    enabled: true,
    status: 'published',
    triggers: [
      {
        eventType: 'mesh://catalog/v1/events/entity_patched',
        eventSpace: '',
        filters: [{ field: 'type', operator: 'equals', value: 'account' }],
        priority: 1,
      },
    ],
    condition: { type: 'value_changed', parameters: { field: 'locked' } },
    output: {
      type: 'event',
      eventOutput: {
        eventType: 'mesh://engine/v1/signals/account_lockout',
        template: '',
        context: {},
      },
    },
    outputs: [
      { eventType: 'mesh://engine/v1/signals/account_lockout', eventSpace: '' },
    ],
  },
  {
    ruleId: 'aaaaaaaa-0000-0000-0000-000000000002' as UUID,
    tenantId: 'aaaaaaaa-0000-0000-0000-000000000000' as UUID,
    name: 'Entity Retired Watcher',
    description: 'Detects when any entity is retired.',
    type: 'derivation',
    enabled: false,
    status: 'draft',
    triggers: [
      {
        eventType: 'mesh://catalog/v1/events/entity_retired',
        eventSpace: '',
        filters: [],
        priority: 1,
      },
    ],
    output: {
      type: 'event',
      eventOutput: {
        eventType: 'mesh://engine/v1/signals/entity_retired',
        template: '',
        context: {},
      },
    },
    outputs: [
      { eventType: 'mesh://engine/v1/signals/entity_retired', eventSpace: '' },
    ],
  },
];

const mockEngineService: typeof engineService = {
  listEvaluators: async (_tenantId, query) => toPage([..._mockStore], query),

  findEvaluator: async (_tenantId, ruleId) =>
    _mockStore.find((e) => e.ruleId === ruleId) ?? null,

  createEvaluator: async (_tenantId, payload) => {
    const id = newUUID();
    _mockStore.push({
      ruleId: id,
      tenantId: _tenantId,
      name: payload.name,
      description: payload.description,
      type: 'derivation',
      enabled: payload.enabled ?? true,
      status: 'draft',
      triggers: [],
      output: {
        type: 'event',
        eventOutput: { eventType: '', template: '', context: {} },
      },
      outputs: [],
    });
    return id;
  },

  updateEvaluator: async (_tenantId, ruleId, updates) => {
    const idx = _mockStore.findIndex((e) => e.ruleId === ruleId);
    if (idx === -1) return false;
    _mockStore[idx] = { ..._mockStore[idx], ...updates } as Evaluator;
    return true;
  },

  deleteEvaluator: async (_tenantId, ruleId) => {
    const idx = _mockStore.findIndex((e) => e.ruleId === ruleId);
    if (idx === -1) return false;
    _mockStore.splice(idx, 1);
    return true;
  },

  changeEvaluatorStatus: async (_tenantId, ruleId, enabled) => {
    const item = _mockStore.find((e) => e.ruleId === ruleId);
    if (!item) return false;
    item.enabled = enabled;
    return true;
  },

  publishEvaluator: async (_tenantId, ruleId) => {
    const item = _mockStore.find((e) => e.ruleId === ruleId);
    if (!item) return null;
    item.status = 'published';
    return item;
  },

  retireEvaluator: async (_tenantId, ruleId) => {
    const item = _mockStore.find((e) => e.ruleId === ruleId);
    if (!item) return null;
    item.status = 'retired';
    return item;
  },

  // Pass automations and config through to real service (not broken)
  listAutomations: engineService.listAutomations,
  findAutomation: engineService.findAutomation,
  createAutomation: engineService.createAutomation,
  updateAutomation: engineService.updateAutomation,
  deleteAutomation: engineService.deleteAutomation,
  changeAutomationStatus: engineService.changeAutomationStatus,
  getEngineConfig: async () => ({
    evaluatorCount: _mockStore.length,
    automationCount: 0,
  }),
};

export const activeEngineService =
  import.meta.env.VITE_MOCK_ENGINE === 'true'
    ? mockEngineService
    : engineService;
